import { useState } from "react";
import { C } from "../constants/colors";

const MEDIUM_MULT = {
  Painting: 1.0,
  Drawing: 0.8,
  Photography: 0.85,
  Printmaking: 0.85,
  Sculpture: 1.2,
  Ceramics: 1.1,
  Textiles: 0.9,
  Digital: 0.75,
  Video: 1.0,
  Installation: 1.2,
  Performance: 1.0,
  "Mixed Media": 1.0,
};

const MARKETS = {
  "Australia (AUD)":      { name: "Australian",  code: "AUD", sym: "$",   index: { Emerging: 16, "Mid-Career": 26, Established: 42 }, commRange: "33–50%" },
  "New Zealand (NZD)":    { name: "New Zealand", code: "NZD", sym: "NZ$", index: { Emerging: 14, "Mid-Career": 22, Established: 36 }, commRange: "33–50%" },
  "United Kingdom (GBP)": { name: "UK",          code: "GBP", sym: "£",   index: { Emerging: 20, "Mid-Career": 34, Established: 55 }, commRange: "40–60%" },
  "United States (USD)":  { name: "US",          code: "USD", sym: "$",   index: { Emerging: 18, "Mid-Career": 30, Established: 50 }, commRange: "40–60%" },
  "Europe (EUR)":         { name: "European",    code: "EUR", sym: "€",   index: { Emerging: 16, "Mid-Career": 28, Established: 45 }, commRange: "40–55%" },
  "Canada (CAD)":         { name: "Canadian",    code: "CAD", sym: "CA$", index: { Emerging: 15, "Mid-Career": 25, Established: 40 }, commRange: "40–55%" },
};

const round50 = (n) => Math.round(n / 50) * 50;

const SEL = {
  width: "100%",
  padding: "12px 14px",
  border: `1.5px solid ${C.sand}`,
  borderRadius: 5,
  background: "#fff",
  fontSize: ".9rem",
  color: C.bark,
  outline: "none",
  minHeight: 48,
  appearance: "none",
  WebkitAppearance: "none",
};

const CARD_LBL = {
  fontFamily: "'DM Mono',monospace",
  fontSize: ".56rem",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: C.muted,
  display: "block",
  marginBottom: 8,
};

const CARD_AMT = {
  fontFamily: "'Fraunces',serif",
  fontSize: "2.2rem",
  color: C.clay,
  fontWeight: 400,
  lineHeight: 1,
};

const CARET = {
  position: "absolute",
  right: 12,
  top: "50%",
  transform: "translateY(-50%)",
  pointerEvents: "none",
  color: C.muted,
  fontSize: ".75rem",
};

export function PricingGuide({ bp }) {
  const [market,        setMarket]        = useState("Australia (AUD)");
  const [medium,        setMedium]        = useState("Painting");
  const [career,        setCareer]        = useState("Emerging");
  const [width,         setWidth]         = useState("");
  const [height,        setHeight]        = useState("");
  const [workType,      setWorkType]      = useState("Original");
  const [editionSize,   setEditionSize]   = useState(10);
  const [materialsCost, setMaterialsCost] = useState("");
  const [framing,       setFraming]       = useState("Unframed");
  const [frameCost,     setFrameCost]     = useState("");
  const [commission,    setCommission]    = useState(40);

  const mkt = MARKETS[market];
  const fmt = (n) => mkt.sym + n.toLocaleString("en");

  const w  = parseFloat(width)         || 0;
  const h  = parseFloat(height)        || 0;
  const mc = parseFloat(materialsCost) || 0;
  const fc = framing === "Framed" ? (parseFloat(frameCost) || 0) : 0;
  const hasSize = w > 0 && h > 0;

  let edAdj = 1.0;
  if (workType === "Limited Edition") edAdj = 1 + 1 / Math.max(1, editionSize);
  if (workType === "Open Edition")    edAdj = 0.4;

  const suggested  = round50((h + w) * mkt.index[career] * MEDIUM_MULT[medium] * edAdj + mc + fc);
  const galleryRRP = round50(suggested / (1 - commission / 100));
  const takeHome   = suggested;

  const isMobile = bp === "mobile";

  return (
    <div>
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontFamily: "'Fraunces',serif", fontSize: "1.7rem", fontWeight: 400, color: C.bark, marginBottom: 4 }}>Pricing Guide</h1>
        <p style={{ fontSize: ".84rem", color: C.muted }}>Calculate a market-informed price for your work.</p>
      </div>

      {/* ── Inputs ── */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14, marginBottom: 24 }}>

        {/* Market — spans both columns */}
        <div className="form-group" style={{ margin: 0, gridColumn: isMobile ? undefined : "1 / -1" }}>
          <label className="form-label">Market</label>
          <div style={{ position: "relative", maxWidth: isMobile ? "100%" : "50%" }}>
            <select style={SEL} value={market} onChange={e => setMarket(e.target.value)}>
              {Object.keys(MARKETS).map(m => <option key={m}>{m}</option>)}
            </select>
            <span style={CARET}>▾</span>
          </div>
        </div>

        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Medium</label>
          <div style={{ position: "relative" }}>
            <select style={SEL} value={medium} onChange={e => setMedium(e.target.value)}>
              {Object.keys(MEDIUM_MULT).map(m => <option key={m}>{m}</option>)}
            </select>
            <span style={CARET}>▾</span>
          </div>
        </div>

        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Career stage</label>
          <div style={{ position: "relative" }}>
            <select style={SEL} value={career} onChange={e => setCareer(e.target.value)}>
              {Object.keys(mkt.index).map(s => <option key={s}>{s}</option>)}
            </select>
            <span style={CARET}>▾</span>
          </div>
        </div>

        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Width (cm)</label>
          <input style={SEL} type="number" min="0" placeholder="e.g. 60" value={width} onChange={e => setWidth(e.target.value)} />
        </div>

        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Height (cm)</label>
          <input style={SEL} type="number" min="0" placeholder="e.g. 90" value={height} onChange={e => setHeight(e.target.value)} />
        </div>

        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Work type</label>
          <div style={{ position: "relative" }}>
            <select style={SEL} value={workType} onChange={e => setWorkType(e.target.value)}>
              <option>Original</option>
              <option>Limited Edition</option>
              <option>Open Edition</option>
            </select>
            <span style={CARET}>▾</span>
          </div>
        </div>

        {workType === "Limited Edition" && (
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Edition size</label>
            <input
              style={SEL}
              type="number"
              min="1"
              placeholder="e.g. 25"
              value={editionSize}
              onChange={e => setEditionSize(Math.max(1, parseInt(e.target.value) || 1))}
            />
          </div>
        )}

        {/* Materials cost */}
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Materials cost ({mkt.code})</label>
          <input
            style={SEL}
            type="number"
            min="0"
            placeholder="0"
            value={materialsCost}
            onChange={e => setMaterialsCost(e.target.value)}
          />
          <div style={{ fontSize: ".72rem", color: C.muted, marginTop: 5, lineHeight: 1.5 }}>Paint, canvas, paper, ink, clay — total cost for this work</div>
        </div>

        {/* Framing */}
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">Framing</label>
          <div style={{ position: "relative" }}>
            <select style={SEL} value={framing} onChange={e => setFraming(e.target.value)}>
              <option>Unframed</option>
              <option>Framed</option>
            </select>
            <span style={CARET}>▾</span>
          </div>
          {/* Frame cost — animates in/out */}
          <div style={{
            overflow: "hidden",
            maxHeight: framing === "Framed" ? 120 : 0,
            opacity: framing === "Framed" ? 1 : 0,
            transition: "max-height .25s ease, opacity .2s ease",
            marginTop: framing === "Framed" ? 10 : 0,
          }}>
            <label className="form-label">Frame cost ({mkt.code})</label>
            <input
              style={SEL}
              type="number"
              min="0"
              placeholder="0"
              value={frameCost}
              onChange={e => setFrameCost(e.target.value)}
            />
            <div style={{ fontSize: ".72rem", color: C.muted, marginTop: 5, lineHeight: 1.5 }}>Cost of the frame for this work</div>
          </div>
        </div>
      </div>

      {/* ── Output cards ── */}
      {hasSize ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>

            <div style={{ background: C.cream, border: `1px solid ${C.sand}`, borderRadius: 8, padding: "20px 22px" }}>
              <span style={CARD_LBL}>Suggested price</span>
              <div style={CARD_AMT}>{fmt(suggested)}</div>
              <div style={{ fontSize: ".72rem", color: C.muted, marginTop: 8, lineHeight: 1.5 }}>For your studio & direct sales</div>
            </div>

            <div style={{ background: C.cream, border: `1px solid ${C.sand}`, borderRadius: 8, padding: "20px 22px" }}>
              <span style={CARD_LBL}>Gallery retail price</span>
              <div style={CARD_AMT}>{fmt(galleryRRP)}</div>
              <div style={{ fontSize: ".72rem", color: C.muted, marginTop: 8, lineHeight: 1.5 }}>List at this price with a gallery</div>
            </div>

            <div style={{ background: C.cream, border: `1px solid ${C.sand}`, borderRadius: 8, padding: "20px 22px" }}>
              <span style={CARD_LBL}>Your take home</span>
              <div style={CARD_AMT}>{fmt(takeHome)}</div>
              <div style={{ fontSize: ".72rem", color: C.muted, marginTop: 8, lineHeight: 1.5 }}>What you keep after commission</div>
            </div>
          </div>

          {/* Commission slider */}
          <div style={{ background: C.cream, border: `1px solid ${C.sand}`, borderRadius: 8, padding: "20px 22px", marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <label style={{ ...CARD_LBL, marginBottom: 0 }}>Gallery commission</label>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <input
                  type="number"
                  min="20"
                  max="60"
                  value={commission}
                  onChange={e => setCommission(Math.min(60, Math.max(20, parseInt(e.target.value) || 40)))}
                  style={{ width: 52, padding: "5px 8px", border: `1.5px solid ${C.sand}`, borderRadius: 4, fontSize: ".85rem", color: C.bark, textAlign: "center", background: "#fff", outline: "none" }}
                />
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: ".72rem", color: C.muted }}>%</span>
              </div>
            </div>
            <input
              type="range"
              min="20"
              max="60"
              value={commission}
              onChange={e => setCommission(parseInt(e.target.value))}
              style={{ width: "100%", accentColor: C.clay, cursor: "pointer" }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "'DM Mono',monospace", fontSize: ".55rem", color: C.muted, marginTop: 5 }}>
              <span>20%</span><span>60%</span>
            </div>
          </div>

          {/* Disclaimer */}
          <div style={{ background: C.parchD, border: `1px solid ${C.sand}`, borderRadius: 8, padding: "16px 18px" }}>
            <p style={{ fontSize: ".8rem", color: C.muted, lineHeight: 1.75, marginBottom: 8 }}>
              This estimate is based on your medium, dimensions, career stage, and the {mkt.name} art market. It's a starting point — compare with similar artists at your career stage and adjust based on your sales history. Prices should be consistent across your studio and gallery listings.
            </p>
            <p style={{ fontSize: ".74rem", color: C.muted, fontFamily: "'DM Mono',monospace" }}>
              {mkt.name} gallery commission typically ranges from {mkt.commRange}.
            </p>
          </div>
        </>
      ) : (
        <div style={{ border: `2px dashed ${C.sand}`, borderRadius: 8, padding: "48px 20px", textAlign: "center" }}>
          <div style={{ fontSize: "2.5rem", marginBottom: 12, opacity: .4 }}>🎨</div>
          <div style={{ fontFamily: "'Fraunces',serif", fontSize: "1rem", color: C.bark, marginBottom: 6 }}>Enter dimensions to calculate</div>
          <p style={{ fontSize: ".8rem", color: C.muted, lineHeight: 1.65 }}>Fill in the width and height above to see your price estimate.</p>
        </div>
      )}
    </div>
  );
}
